import { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Loader,
  Activity,
  ArrowRightLeft,
  UserCheck,
  Plus,
  Trash2,
  MessageSquare,
  Award,
  Tag,
  CalendarClock,
  ClipboardCheck,
  Workflow,
  ShieldCheck,
  X,
  ChevronDown,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import type { EventActivityLog as EventActivityLogEntry } from '../../types/eventTeam';
import { eventTeamApiService } from '../../services/eventTeamApi';
import { usernameCacheService } from '../../services/usernameCache';
import { CreatorProfile } from '../CreatorProfile';
import { cn } from '../../utils/cn';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonLoader } from '../SkeletonLoader';

const ACTION_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  status_change: ArrowRightLeft,
  assigned: UserCheck,
  self_assigned: UserCheck,
  created: Plus,
  deleted: Trash2,
  comment: MessageSquare,
  points_awarded: Award,
  prefix_granted: ShieldCheck,
  tagged: Tag,
  scheduled: CalendarClock,
  task_completed: ClipboardCheck,
  workflow_applied: Workflow,
  retro_updated: ClipboardCheck,
  check_results_submitted: ShieldCheck,
};

function getActionIcon(action: string) {
  if (ACTION_ICONS[action]) return ACTION_ICONS[action];
  for (const [key, icon] of Object.entries(ACTION_ICONS)) {
    if (action.includes(key)) return icon;
  }
  return Activity;
}

export type EventActivitySource =
  | { kind: 'event'; id: string }
  | { kind: 'project'; id: string }
  | { kind: 'member'; id: string }
  | { kind: 'recent'; limit?: number }
  | { kind: 'actor'; actorId: string };

export interface EventActivityLogProps {
  source: EventActivitySource;
  /** Bumped by parents (e.g. WS handlers) to trigger a refetch. */
  refreshNonce?: number;
  /** Enables the per-actor drill-down modal on actor click (dashboard feed). */
  enableActorDrilldown?: boolean;
  pageSize?: number;
}

function fetchActivity(source: EventActivitySource, page: number, limit: number): Promise<EventActivityLogEntry[]> {
  switch (source.kind) {
    case 'event':
      return eventTeamApiService.getEventActivity(source.id, page, limit);
    case 'project':
      return eventTeamApiService.getProjectActivity(source.id, page, limit);
    case 'member':
      return eventTeamApiService.getTeamMemberActivity(source.id, page, limit);
    case 'recent':
      return eventTeamApiService.getRecentActivity(source.limit ?? limit);
    case 'actor':
      return eventTeamApiService.getActivityByActor(source.actorId, page, limit);
  }
}

function sourceKey(source: EventActivitySource): string {
  switch (source.kind) {
    case 'event':
    case 'project':
    case 'member':
      return `${source.kind}:${source.id}`;
    case 'recent':
      return `recent:${source.limit ?? ''}`;
    case 'actor':
      return `actor:${source.actorId}`;
  }
}

/**
 * Timeline-styled activity list backed by the `event_activity_log`.
 * Supports event/project/member/recent/actor sources with load-more pagination
 * (recent mode is a single fetch — the endpoint takes `limit` only).
 */
export function EventActivityLog({
  source,
  refreshNonce = 0,
  enableActorDrilldown = false,
  pageSize = 25,
}: EventActivityLogProps) {
  const navigate = useNavigate();
  const [entries, setEntries] = useState<EventActivityLogEntry[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [drilldownActor, setDrilldownActor] = useState<string | null>(null);

  const key = sourceKey(source);

  const load = useCallback(
    async (targetPage: number, append: boolean) => {
      if (append) setLoadingMore(true);
      else setLoading(true);
      setError(null);
      try {
        const data = await fetchActivity(source, targetPage, pageSize);
        setEntries((prev) => (append ? [...prev, ...data] : data));
        setHasMore(source.kind !== 'recent' && data.length === pageSize);
        setPage(targetPage);
        const actorIds = data.map((e) => e.actor_id).filter(Boolean);
        if (actorIds.length > 0) usernameCacheService.prefetchUsernames(actorIds);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load activity');
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key, pageSize],
  );

  useEffect(() => {
    void load(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, refreshNonce]);

  const navigateToTarget = (entry: EventActivityLogEntry) => {
    if (entry.target_type === 'event') navigate(`/event-team/events/${entry.target_id}`);
    else if (entry.target_type === 'project') navigate(`/event-team/projects/${entry.target_id}`);
    else if (entry.target_type === 'team_member') navigate(`/event-team/members/${entry.target_id}`);
    else if (entry.target_type === 'check') navigate(`/event-team/checks/${entry.target_id}`);
  };

  const isNavigable = (entry: EventActivityLogEntry) =>
    ['event', 'project', 'team_member', 'check'].includes(entry.target_type);

  if (loading) {
    return (
      <div className="space-y-4" aria-busy="true" aria-label="Loading activity">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex gap-4">
            <SkeletonLoader className="w-6 h-6 rounded-full shrink-0" />
            <div className="flex-1 space-y-1.5">
              <SkeletonLoader className="h-3.5 w-3/4" />
              <SkeletonLoader className="h-3 w-40" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        <span className="flex-1">{error}</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {entries.length === 0 ? (
        <EmptyState bare compact icon={Activity} title="No activity" />
      ) : (
        <div className="relative">
          <div className="absolute left-[11px] top-2 bottom-2 w-px bg-glass-200/30" />
          <div className="space-y-0">
            {entries.map((entry) => {
              const Icon = getActionIcon(entry.action);
              const hasValueChange = entry.old_value != null && entry.new_value != null;
              return (
                <motion.div
                  key={entry.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.12 }}
                  className="relative flex gap-4 pb-6 last:pb-0"
                >
                  <div className="relative z-10 flex-shrink-0 w-6 h-6 rounded-full bg-glass-200/50 border-2 border-accent-500/50 flex items-center justify-center">
                    <Icon className="w-3 h-3 text-accent-400" />
                  </div>

                  <div className="flex-1 min-w-0 pt-0.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={cn(
                          'text-sm text-glass-200',
                          isNavigable(entry) && 'cursor-pointer hover:text-accent-300 transition-colors',
                        )}
                        onClick={() => isNavigable(entry) && navigateToTarget(entry)}
                      >
                        {entry.description}
                      </span>
                      {hasValueChange && (
                        <span className="text-xs text-glass-500 font-mono">
                          {entry.old_value} → {entry.new_value}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {enableActorDrilldown ? (
                        <button
                          type="button"
                          onClick={() => setDrilldownActor(entry.actor_id)}
                          className="hover:brightness-125 transition-all rounded"
                          title="View this person's event-team activity"
                        >
                          <CreatorProfile creatorId={entry.actor_id} inline />
                        </button>
                      ) : (
                        <CreatorProfile creatorId={entry.actor_id} inline />
                      )}
                      <span className="text-xs text-glass-500 tabular-nums">
                        {formatDistanceToNow(new Date(entry.created_at), { addSuffix: true })}
                      </span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      )}

      {hasMore && (
        <button
          type="button"
          onClick={() => void load(page + 1, true)}
          disabled={loadingMore}
          className="glass-button-secondary btn-xs w-full"
        >
          {loadingMore ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <ChevronDown className="w-3.5 h-3.5" />}
          Load more
        </button>
      )}

      {/* Per-actor drill-down modal (guide §1.1 "Activity by person") */}
      {drilldownActor && (
        <div
          className="modal-overlay z-50"
          onClick={() => setDrilldownActor(null)}
        >
          <div
            className="modal-panel max-w-2xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Activity by person"
          >
            <div className="modal-header">
              <h3 className="modal-title inline-flex items-center gap-2">
                <Activity className="w-5 h-5 text-accent-400" />
                Activity by <CreatorProfile creatorId={drilldownActor} inline />
              </h3>
              <button
                type="button"
                onClick={() => setDrilldownActor(null)}
                className="modal-close"
                title="Close"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="modal-body">
              <EventActivityLog source={{ kind: 'actor', actorId: drilldownActor }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
